import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Check, ArrowRight, Home, Download, Calendar, Receipt, Building2, CreditCard, Mail, Phone } from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { useApiQuery } from '../../hooks/useApiQuery';
import { Receipt as ReceiptIcon } from '@mui/icons-material';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface PaymentStatusResponse {
  success: boolean;
  data: {
    reference: string;
    status: string;
    amount: number;
    currency: string;
    planName: string;
    createdAt: Date;
    updatedAt?: Date | null;
    nextBillingDate?: string;
    customerEmail?: string;
    paymentMethod?: string;
    metadata?: {
      plan: string;
    }
  };
}

interface LocationState {
  plan: {
    name: string;
    price: string;
    period: string;
    description: string;
    amount: string;
  };
  paymentId: string;
  amount: string;
  timestamp: string;
}

const PartnerUpgradeSuccess: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const state = location.state as LocationState;
  const receiptRef = useRef<HTMLDivElement>(null);

  const reference = searchParams.get('reference');

  // Helper function to get plan amount based on plan name
  const getPlanAmount = (planName: string): string => {
    switch (planName?.toLowerCase()) {
      case 'community':
        return '99';
      case 'field_pro':
        return '499';
      case 'partner_growth':
        return '1250';
      case 'campaign':
        return '2500';
      case 'free':
        return '0';
      default:
        return 'N/A';
    }
  };

  const { data: paymentStatus, isLoading, error } = useApiQuery<PaymentStatusResponse>({
    queryKey: ['payment-status', reference],
    request: {
      url: `/partner/payments/status?reference=${reference}`,
      method: 'GET',
    },
    enabled: !!reference, // Only fetch if reference exists
  });


  const handleDownloadReceipt = async () => {
    if (!receiptRef.current || !paymentStatus?.data) {
      toast.error('Receipt data not available');
      return;
    }

    try {
      toast.loading('Generating receipt...', { id: 'receipt' });
      
      // Capture the receipt element as canvas
      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
      });

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      // Calculate dimensions to fit the receipt in the PDF
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight) * 200;
      const imgX = (pdfWidth - imgWidth * ratio / 200) / 2;
      const imgY = 10;

      // Add image to PDF
      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio / 200, imgHeight * ratio / 200);
      
      // Generate filename
      const planName = paymentStatus.data.metadata?.plan || 'upgrade';
      const date = format(new Date(), 'yyyy-MM-dd');
      const filename = `123-VOTE-${planName}-receipt-${date}.pdf`;
      
      // Download PDF
      pdf.save(filename);
      
      toast.success('Receipt downloaded successfully!', { id: 'receipt' });
    } catch (error) {
      console.error('Error generating receipt:', error);
      toast.error('Failed to generate receipt. Please try again.', { id: 'receipt' });
    }
  };

  const handleGoToDashboard = () => {
    navigate('/partner/dashboard');
  };

  // Hidden Receipt Component for PDF Generation
  const ReceiptComponent = () => (
    <div 
      ref={receiptRef} 
      className="fixed -left-[9999px] top-0 w-[800px] bg-white p-8"
      style={{ fontFamily: 'Arial, sans-serif' }}
    >
      {/* Header */}
      <div className="border-b-2 border-gray-800 pb-6 mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">123-VOTE</h1>
            <p className="text-gray-600">Payment Receipt</p>
          </div>
          <div className="text-right">
            <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold mb-2">
              PAID
            </div>
            <p className="text-sm text-gray-600">Receipt #: {paymentStatus?.data?.reference}</p>
          </div>
        </div>
      </div>

      {/* Payment Details */}
      <div className="grid grid-cols-2 gap-8 mb-6">
        <div>
          <h3 className="font-semibold text-gray-900 mb-3">Payment Details</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Date:</span>
              <span className="text-gray-900">
                {paymentStatus?.data?.updatedAt ? 
                  format(new Date(paymentStatus.data.updatedAt), 'MMMM d, yyyy h:mm a') :
                  format(new Date(), 'MMMM d, yyyy h:mm a')
                }
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Payment Method:</span>
              <span className="text-gray-900">{paymentStatus?.data?.paymentMethod || 'Credit Card'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Status:</span>
              <span className="text-green-600 font-semibold">{paymentStatus?.data?.status || 'Completed'}</span>
            </div>
          </div>
        </div>

        <div>
          <h3 className="font-semibold text-gray-900 mb-3">Billing Information</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Email:</span>
              <span className="text-gray-900">{paymentStatus?.data?.customerEmail || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Plan:</span>
              <span className="text-gray-900 font-semibold">
                {paymentStatus?.data?.metadata?.plan || state?.plan?.name}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Billing Cycle:</span>
              <span className="text-gray-900">Monthly</span>
            </div>
          </div>
        </div>
      </div>

      {/* Order Summary */}
      <div className="border-t border-b border-gray-300 py-4 mb-6">
        <h3 className="font-semibold text-gray-900 mb-3">Order Summary</h3>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">
              {paymentStatus?.data?.metadata?.plan || state?.plan?.name} Plan - Monthly Subscription
            </span>
            <span className="text-gray-900">
              ${getPlanAmount(paymentStatus?.data?.metadata?.plan || state?.plan?.name)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Subtotal</span>
            <span className="text-gray-900">
              ${getPlanAmount(paymentStatus?.data?.metadata?.plan || state?.plan?.name)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Tax</span>
            <span className="text-gray-900">$0.00</span>
          </div>
          <div className="flex justify-between font-semibold text-base pt-2 border-t">
            <span className="text-gray-900">Total</span>
            <span className="text-gray-900">
              ${getPlanAmount(paymentStatus?.data?.metadata?.plan || state?.plan?.name)}
            </span>
          </div>
        </div>
      </div>

      {/* Next Billing */}
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <h3 className="font-semibold text-gray-900 mb-2">Next Billing</h3>
        <p className="text-sm text-gray-600">
          Your next billing date is{' '}
          <span className="font-semibold text-gray-900">
            {paymentStatus?.data?.nextBillingDate ?
              format(new Date(paymentStatus.data.nextBillingDate), 'EEEE, MMMM d, yyyy') :
              new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })
            }
          </span>
        </p>
        <p className="text-sm text-gray-600 mt-1">
          Amount: <span className="font-semibold text-gray-900">
            ${getPlanAmount(paymentStatus?.data?.metadata?.plan || state?.plan?.name)}
          </span>
        </p>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-300 pt-4 text-center text-xs text-gray-500">
        <p>Thank you for choosing 123-VOTE!</p>
        <p className="mt-1">For support, email: support@123vote.com</p>
        <p className="mt-2">This is a computer-generated receipt and does not require a signature.</p>
      </div>
    </div>
  );



  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payment details...</p>
        </div>
      </div>
    );
  }

  // Show error state if payment status fetch fails
  if (error || (paymentStatus && !paymentStatus.success)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Verification Failed</h1>
          <p className="text-gray-600 mb-6">
            We couldn't verify your payment status. Please contact support if this issue persists.
          </p>
          <button
            onClick={handleGoToDashboard}
            className="bg-blue-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-700 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }




  return (
    <>
      {/* Hidden Receipt Component for PDF Generation */}
      <ReceiptComponent />
      
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 py-10 px-4 sm:px-6 lg:px-6">
      <div className="max-w-4xl mx-auto">
        {/* Success Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5, type: "spring" }}
          >
            <Check className="w-10 h-10 text-green-600" />
          </motion.div>

          <h1 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-4">
            Payment Successful!
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Your plan has been successfully upgraded
          </p>
        </motion.div>

        {/* Main Content */}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Upgrade Confirmation</h2>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">New Plan</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {paymentStatus?.data?.metadata?.plan || state?.plan?.name}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Amount</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {
                      `$${getPlanAmount(paymentStatus?.data?.metadata?.plan || state?.plan?.name)}/month`
                    }
                  </p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-900 mb-3">Payment Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment Reference:</span>
                    <span className="font-mono text-gray-900">{paymentStatus?.data?.reference || state?.paymentId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment made on:</span>
                    <span className="text-gray-900">
                      {
                        paymentStatus?.data?.updatedAt ? 
                          format(new Date(paymentStatus.data.updatedAt), 'MMMM d, yyyy h:mm a') :
                          'N/A'
                      }
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2" />
                      {paymentStatus?.data?.status || 'Completed'}
                    </span>
                  </div>
                  {paymentStatus?.data?.paymentMethod && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Payment Method:</span>
                      <span className="text-gray-900">{paymentStatus.data.paymentMethod}</span>
                    </div>
                  )}
                  {paymentStatus?.data?.customerEmail && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Email:</span>
                      <span className="text-gray-900">{paymentStatus.data.customerEmail}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-900 mb-3">What's Next?</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Your account has been upgraded immediately</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>All new features are now available in your dashboard</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>You'll receive a confirmation email shortly</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Billing will renew automatically each month</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Plan Features & Actions */}
          <div className="space-y-6">
            {/* New Features */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Your New Features</h2>

              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold text-blue-900 mb-2">
                    Welcome to {paymentStatus?.data?.planName || state?.plan?.name}!
                  </h3>
                  <p className="text-blue-800 text-sm mb-3">
                    {state?.plan?.description || 'Thank you for upgrading your plan!'}
                  </p>

                  <div className="space-y-2">
                    <p className="text-sm font-medium text-blue-900">Key Benefits:</p>
                    <ul className="space-y-1 text-sm text-blue-800">
                      <li>• Enhanced API access limits</li>
                      <li>• Advanced export capabilities</li>
                      <li>• Priority customer support</li>
                      <li>• Compliance-ready features</li>
                    </ul>
                  </div>
                </div>

                <div className="p-4 bg-amber-50 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <strong>Tip:</strong> Explore your new features in the dashboard settings to maximize your organization's impact.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Get Started</h2>

              <div className="space-y-3">
                <motion.button
                  onClick={handleGoToDashboard}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-3 px-6 rounded-full font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-300 flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Home className="w-5 h-5" />
                  Go to Dashboard
                  <ArrowRight className="w-4 h-4" />
                </motion.button>

                <motion.button
                  onClick={handleDownloadReceipt}
                  className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-full font-semibold hover:bg-gray-200 transition-all duration-300 flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Receipt className="w-5 h-5" />
                  Download Receipt
                </motion.button>


              </div>
            </div>
          </div>
        </motion.div>

        {/* Billing Information */}
        <motion.div
          className="mt-8 bg-white rounded-2xl shadow-lg border border-gray-200 p-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <h2 className="text-xl font-bold text-gray-900 mb-4">Billing Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Current Billing Cycle</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Plan:</span>
                  <span className="font-medium text-gray-900">
                    {paymentStatus?.data?.metadata?.plan || state?.plan?.name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Monthly Cost:</span>
                  <span className="font-medium text-gray-900">
                     {
                      `$${getPlanAmount(paymentStatus?.data?.metadata?.plan || state?.plan?.name)}/month`
                    }
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Next Billing Date:</span>
                  <span className="font-medium text-gray-900">
                    {paymentStatus?.data?.nextBillingDate ?
                      format(new Date(paymentStatus.data.nextBillingDate), 'EEE, dd MMM, yyyy') :
                      new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()
                    }
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Need Help?</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p>• Contact our support team for any billing questions</p>
                <p>• View detailed usage analytics in your dashboard</p>
                <p>• Manage your subscription settings anytime</p>
              </div>
              <div className="mt-4">
                <a
                  href="mailto:support@123vote.com"
                  className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center gap-1"
                >
                  Contact Support
                  <ArrowRight className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          className="text-center mt-12 text-gray-500 text-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.5 }}
        >
          <p>Thank you for choosing 123-VOTE! We're excited to help you make a difference.</p>
        </motion.div>
      </div>
    </div>
    </>
  );
};

export default PartnerUpgradeSuccess;
